import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button } from '@/components/atoms'
import { Identification } from '@/components/organisms/customer/identification/identification'
import { CustomerType } from '@/models/customer-manager/customer'
import * as api from '@/apis/aipass'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { UpdateGuestParams } from '@/models/guest-input'
import { convertToSeparatedName } from '@/libs/convertToSeparatedName'
import { EditForm } from '@/components/molecules/customer/edit-form'
import { useFileUpload } from '@/hooks/use-file-upload'
import { updateCheckinIdentification } from '@/apis/aipass'
import { AccompanyForm } from '@/components/molecules/customer/accompany-form'
import { AddFormFieldButton } from '@/components/molecules/add-form-field-button'

type ComponentProps = {
  customer: CustomerType
  isCustomerLinked: boolean
  onCancel: () => void
  onSave: () => void
}

const createDefaultAccompany = (): UpdateGuestParams['accompanyList'][number] => ({
  id: '',
  hotelCuicinUserId: undefined,
  nationality: 'JPN',
  firstName: '',
  lastName: '',
  firstNameKana: '',
  lastNameKana: '',
  birthDate: '',
  gender: 'M',
  address: '',
  postalCode: '',
  telephone: '',
  email: '',
  occupation: '',
  company: '',
  otherOccupation: '',
  deliveryAgreementStatus: 'unconfirmed',
})

export const AddGuestInfoModal: React.FC<ComponentProps> = props => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const { upload } = useFileUpload()

  const [customerFirstName, customerLastName] = convertToSeparatedName({ name: props.customer.name || '' })
  const [customerFirstNameKana, customerLastNameKana] = convertToSeparatedName({ name: props.customer.nameKana || '' })

  const formReturn = useForm<UpdateGuestParams>({
    defaultValues: {
      id: props.customer.id,
      nationality: props.customer.nationality,
      lastName: customerLastName || '',
      firstName: customerFirstName || '',
      lastNameKana: customerLastNameKana || '',
      firstNameKana: customerFirstNameKana || '',
      birthDate: props.customer.birthDate || '',
      gender: props.customer.gender || '',
      address: props.customer.address || '',
      postalCode: props.customer.postalCode || '',
      telephone: props.customer.telephone || '',
      email: props.customer.email || '',
      occupation: props.customer.occupation || '',
      company: props.customer.company || '',
      otherOccupation: props.customer.otherOccupation || '',
      deliveryAgreementStatus: props.customer.deliveryAgreementStatus || 'unconfirmed',
      accompanyList: props.customer.accompanyList || [],
    },
  })
  const accompanyFields = useFieldArray({ control: formReturn.control, name: 'accompanyList', keyName: '_key' })

  const [identifications, setIdentifications] = useState<any[]>(props.customer.identification || [])
  const [deleteIdentifications, setDeleteIdentifications] = useState<string[]>([])

  const handleAddIdentification = (newItem: { file: File; previewUrl: string; tempId: string }) => {
    setIdentifications([...identifications, newItem])
  }

  const handleDeleteIdentification = (id: string) => {
    if (identifications.find(v => v.identificationId === id)) {
      setDeleteIdentifications([...deleteIdentifications, id])
    }
    setIdentifications(identifications.filter(v => v.identificationId !== id && v.tempId !== id))
  }

  const onSave = async (formValue: UpdateGuestParams) => {
    setIsLoading(true)
    try {
      await api.updateGuestInfo(formValue)

      const uploadResults: {
        checkinId: string
        name: string
        imageKey: string
        isDriversLicense: boolean
      }[] = []
      for (const id of identifications) {
        if (id.file) {
          const { objectKey } = await upload({
            hotelId: props.customer.hotelId,
            file: id.file,
            fileData: id.file,
            feature: 'checkin-image',
          })
          uploadResults.push({
            checkinId: props.customer.id,
            name: id.file.name,
            imageKey: objectKey,
            isDriversLicense: false,
          })
        }
      }
      if (uploadResults.length > 0) {
        await updateCheckinIdentification(uploadResults)
      }
      if (deleteIdentifications.length) {
        await api.deleteCheckinIdentification(deleteIdentifications)
      }

      props.onSave()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FormProvider {...formReturn}>
      <div css={modalWrapperStyle}>
        <div css={modalContentStyle}>
          <div css={modalTitleWrapperStyle}>
            <p css={modalTitleStyle}>{t('Edit check-in information')}</p>
          </div>
          <div css={addGuestInfoWrapperStyle}>
            <div>
              <div css={addGuestInfoStyle}>
                <EditForm />
              </div>

              <div css={addGuestInfoStyle}>
                <div>
                  <div css={accompanyHeaderStyle}>
                    <div className="label">{t('Companion')}</div>
                  </div>
                  <div>
                    {accompanyFields.fields.map((item, index) => (
                      <div key={item._key} css={accompanyItemStyle}>
                        <AccompanyForm index={index} onDelete={() => accompanyFields.remove(index)} />
                      </div>
                    ))}
                  </div>
                  <div>
                    <AddFormFieldButton label={t('Add companion')} onAdd={() => accompanyFields.append(createDefaultAccompany())} />
                  </div>
                </div>
              </div>
            </div>

            <Identification
              checkinId={props.customer.id}
              identifications={identifications}
              hotelId={props.customer.hotelId}
              onClickDeleteImage={handleDeleteIdentification}
              onAddImage={handleAddIdentification}
            />
          </div>

          <div css={footerButtonWrapperStyle}>
            <Button width={110} height={38} marginRight={16} buttonType={3} onClick={props.onCancel}>
              {t('Cancel')}
            </Button>
            <Button width={110} height={38} buttonType={1} onClick={formReturn.handleSubmit(onSave)}>
              {t('Save')}
            </Button>
          </div>
        </div>
      </div>
    </FormProvider>
  )
}

const modalWrapperStyle = css({
  boxShadow: '0px 0px 6px #0000001A',
  border: '1px solid #F2F2F2',
  borderRadius: 5,
  marginRight: 16,
  marginBottom: 16,
  width: '100vw',
  height: '100vh',
  background: 'rgba(39, 39, 39, 0.5)',
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 103,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'scroll',
})

const modalContentStyle = css({
  width: 900,
  maxHeight: 650,
  background: '#fff',
  borderRadius: 5,
  marginBottom: 40,
})

const modalTitleWrapperStyle = css({
  width: 'inherit',
  paddingLeft: 32,
  position: 'absolute',
  'z-index': '10000',
  backgroundColor: 'white',
  borderRadius: '5px 5px 0px 0px',
})

const modalTitleStyle = css({
  color: '#272727',
  fontSize: 18,
  fontWeight: 'bold',
  lineHeight: '60px',
  letterSpacing: '0.9px',
})

const addGuestInfoWrapperStyle = css({
  width: '100%',
  padding: '60px 32px 32px',
  background: '#f2f2f2',
  display: 'flex',
  justifyContent: 'space-between',
  borderRadius: '5px 5px 0px 0px',
})

const footerButtonWrapperStyle = css({
  height: 60,
  paddingRight: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: 'inherit',
  position: 'absolute',
  'z-index': '10000',
  backgroundColor: 'white',
  borderRadius: '0px 0px 5px 5px',
})
const addGuestInfoStyle = css({
  width: 445,
  padding: 32,
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 5,
  marginTop: 24,
  '.label': {
    marginBottom: 12,
    color: '#676767',
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: '0.6px',
  },
})

const accompanyHeaderStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const accompanyItemStyle = css({
  '& + &': {
    marginTop: 8,
  },
})
